import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Col, Container, Nav, Row } from 'react-bootstrap';
import Navbar from '../../Navbar';
import PageTitle from '../../components/PageTitle';
import './guides.css';

const SIDE_LINKS = [
  { to: '/guides/mine', label: 'My Notebooks', icon: 'bi-journal-bookmark', key: 'mine' },
  { to: '/guides/flashcards', label: 'Flashcards', icon: 'bi-layers', key: 'flashcards' },
  { to: '/guides/discover', label: 'Discover', icon: 'bi-compass', key: 'discover' },
  { to: '/guides/shared', label: 'Shared with Me', icon: 'bi-share', key: 'shared' },
  { to: '/guides/settings', label: 'Settings', icon: 'bi-gear', key: 'settings', manageOnly: true }
];

const GUIDES_VIEW_KEY = 'guidesView';
const MANAGE_ROLES = ['director', 'admin', 'administrative assistant'];

function readStoredGuidesView() {
  try {
    const v = localStorage.getItem(GUIDES_VIEW_KEY);
    if (v === 'list' || v === 'grid') return v;
  } catch {
    // ignore
  }
  return 'list';
}

function readUserRole() {
  try {
    const direct = localStorage.getItem('role');
    if (direct) return direct;
    return JSON.parse(localStorage.getItem('user') || '{}')?.role || null;
  } catch {
    return null;
  }
}

export default function GuidesLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState(readStoredGuidesView);
  const canManageSettings = useMemo(() => MANAGE_ROLES.includes(readUserRole()), []);

  const navLinks = useMemo(
    () => SIDE_LINKS.filter((link) => !link.manageOnly || canManageSettings),
    [canManageSettings]
  );

  const setViewMode = useCallback((next) => {
    const v = next === 'list' ? 'list' : 'grid';
    setView(v);
    try {
      localStorage.setItem(GUIDES_VIEW_KEY, v);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/guides/settings') && !canManageSettings) {
      navigate('/guides/mine', { replace: true });
    }
  }, [location.pathname, canManageSettings, navigate]);

  const isListRoute =
    location.pathname === '/guides' ||
    location.pathname === '/guides/mine' ||
    location.pathname === '/guides/discover' ||
    location.pathname === '/guides/shared' ||
    location.pathname === '/guides/flashcards' ||
    (location.pathname === '/guides/settings' && canManageSettings);

  const activeKey =
    location.pathname.includes('settings')
      ? 'settings'
      : location.pathname.includes('flashcards')
        ? 'flashcards'
        : location.pathname.includes('discover')
          ? 'discover'
          : location.pathname.includes('shared')
            ? 'shared'
            : 'mine';

  if (!isListRoute) {
    return <Outlet />;
  }

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle="Guides" />
      <PageTitle title="Guides" description="Notebooks and articles for your program" />

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          <Col lg={2} md={3} className="d-none d-md-block border-end guides-topic-editor-rail">
            <div className="mt-4">
              <div className="smaller text-uppercase text-muted fw-semibold mb-2">Browse</div>
              <Nav variant="pills" className="flex-column mb-0 small">
                {navLinks.map((link) => (
                  <Nav.Item key={link.key}>
                    <Nav.Link
                      active={activeKey === link.key}
                      onClick={() => navigate(link.to)}
                      className="d-flex align-items-center"
                    >
                      <i className={`bi ${link.icon} me-2`} />
                      {link.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
          </Col>
          <Col xs={12} className="d-md-none">
            <div className="p-2">
              <Nav variant="pills" className="flex-row flex-nowrap overflow-auto gap-1">
                {navLinks.map((link) => (
                  <Nav.Item key={link.key}>
                    <Nav.Link
                      active={activeKey === link.key}
                      onClick={() => navigate(link.to)}
                      className="text-nowrap"
                    >
                      <i className={`bi ${link.icon} me-1`} />
                      {link.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>
          </Col>
          <Col lg={10} md={9} xs={12} className="guides-topic-editor-col">
            <div className="guides-read-scroll pt-3 pe-0">
              <Outlet context={{ view, setViewMode }} />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
